/**
 * Iter 67ef — Per-car iCal Channel Sync panel.
 *
 * Vendor pastes the unique read-only **export** URL into OTAs (Booking.com,
 * Turo, Getaround) so those platforms see RentalCar.al's bookings as
 * "blocked" dates. They paste each OTA's **import** URL back in here; we
 * poll every 15 min and turn the foreign events into unavailable_ranges
 * tagged `source = ical:{feed_id}`, which the booking conflict checker
 * already respects — no double-booking risk.
 *
 * Hidden on car create (we need a saved car id to mint the export token).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Copy,
  Check,
  RefreshCw,
  Trash2,
  Plus,
  Link2,
  CalendarSync,
  CircleAlert,
  CircleCheck,
} from "lucide-react";

function _statusLabel(status, error) {
  if (status === "ok") return { tone: "ok", label: "Synced", Icon: CircleCheck };
  if (status === "error") return { tone: "err", label: error || "Sync failed", Icon: CircleAlert };
  return { tone: "idle", label: "Not synced yet", Icon: CalendarSync };
}

function _formatLastSync(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function CarChannelSyncPanel({ carId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [addLabel, setAddLabel] = useState("");
  const [addUrl, setAddUrl] = useState("");
  const [addChannel, setAddChannel] = useState("booking_com");
  const [addError, setAddError] = useState(null);

  const refresh = async () => {
    try {
      const { data: payload } = await api.get(`/vendor/cars/${carId}/channel-sync`);
      setData(payload);
    } catch {
      setData(null);
    }
  };

  useEffect(() => {
    if (!carId) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carId]);

  if (!carId) return null;

  const copyExport = async () => {
    if (!data?.export?.url) return;
    try {
      await navigator.clipboard.writeText(data.export.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* user cancelled clipboard */ }
  };

  const rotateToken = async () => {
    if (!window.confirm("Rotating the export URL will break any OTA subscription using the current link. Continue?")) return;
    setBusy(true);
    try {
      await api.post(`/vendor/cars/${carId}/channel-sync/rotate-token`);
      await refresh();
    } finally { setBusy(false); }
  };

  const addFeed = async () => {
    setAddError(null);
    if (!addLabel.trim() || !addUrl.trim()) {
      setAddError("Label and URL are both required.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/vendor/cars/${carId}/channel-sync/feeds`, {
        label: addLabel.trim(),
        url: addUrl.trim(),
        channel: addChannel,
      });
      setAddLabel(""); setAddUrl(""); setAddChannel("booking_com");
      await refresh();
    } catch (e) {
      setAddError(e?.response?.data?.detail || "Could not add feed.");
    } finally { setBusy(false); }
  };

  const syncNow = async (feedId) => {
    setBusy(true);
    try {
      await api.post(`/vendor/cars/${carId}/channel-sync/feeds/${feedId}/sync`);
      await refresh();
    } finally { setBusy(false); }
  };

  const deleteFeed = async (feedId, label) => {
    if (!window.confirm(`Remove "${label}" and clear all blocks imported from it?`)) return;
    setBusy(true);
    try {
      await api.delete(`/vendor/cars/${carId}/channel-sync/feeds/${feedId}`);
      await refresh();
    } finally { setBusy(false); }
  };

  if (!data) {
    return (
      <div className="rounded-2xl border border-border p-4 bg-neutral-50/40" data-testid="car-channel-sync-loading">
        <div className="text-sm text-neutral-500">Loading channel sync…</div>
      </div>
    );
  }

  const feeds = data.feeds || [];
  const maxFeeds = data.limits?.max_feeds_per_car || 5;
  const atLimit = feeds.length >= maxFeeds;
  const presets = data.presets || [];

  return (
    <div
      className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50/60 to-white p-5"
      data-testid="car-channel-sync"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-sky-100 ring-1 ring-sky-200 flex items-center justify-center shrink-0">
          <CalendarSync className="h-5 w-5 text-sky-700" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg font-semibold">Channel sync (iCal)</div>
          <p className="text-xs text-neutral-600 mt-1 leading-relaxed">
            Keep this car's availability in sync with Booking.com, Turo,
            Getaround and other channels. The <strong>export URL</strong>{" "}
            below shows RentalCar.al bookings to outside platforms; the{" "}
            <strong>import feeds</strong> below block those platforms'
            dates here. Polls every {data.limits?.poll_interval_minutes || 15} min.
          </p>
        </div>
      </div>

      {/* Export URL */}
      <div className="rounded-xl border border-sky-200 bg-white p-4 mb-5" data-testid="car-channel-sync-export">
        <div className="flex items-center justify-between gap-3 mb-2">
          <Label className="text-xs uppercase tracking-wider font-bold text-sky-800">
            Export URL — paste into Booking.com / Turo / etc.
          </Label>
          <button
            type="button"
            onClick={rotateToken}
            disabled={busy}
            className="inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-rose-600 transition"
            data-testid="car-channel-sync-rotate"
          >
            <RefreshCw className="h-3 w-3" /> Rotate URL
          </button>
        </div>
        <div className="flex items-stretch gap-2">
          <Input
            readOnly
            value={data.export.url}
            className="font-mono text-xs flex-1 bg-neutral-50"
            data-testid="car-channel-sync-export-url"
            onFocus={(e) => e.target.select()}
          />
          <Button
            type="button"
            onClick={copyExport}
            className="rounded-xl bg-sky-600 hover:bg-sky-700 text-white whitespace-nowrap shrink-0"
            data-testid="car-channel-sync-copy"
          >
            {copied ? <><Check className="h-4 w-4 mr-1.5" /> Copied</> : <><Copy className="h-4 w-4 mr-1.5" /> Copy</>}
          </Button>
        </div>
        <p className="text-[11px] text-neutral-500 mt-2">
          Read-only feed. Only emits "Booked" dates — never renter names or contact details.
        </p>
      </div>

      {/* Import feeds list */}
      <div className="space-y-2 mb-4" data-testid="car-channel-sync-feeds-list">
        {feeds.length === 0 && (
          <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50/40 p-4 text-center">
            <Link2 className="h-5 w-5 mx-auto text-neutral-400 mb-1" />
            <div className="text-sm text-neutral-600">No import feeds yet</div>
            <div className="text-[11px] text-neutral-500 mt-0.5">Add a Booking.com / Turo / Getaround iCal URL below to block their bookings here.</div>
          </div>
        )}
        {feeds.map((f) => {
          const s = _statusLabel(f.last_status, f.last_error);
          const StatusIcon = s.Icon;
          const toneCls = s.tone === "ok"
            ? "text-emerald-700 bg-emerald-50 border-emerald-200"
            : s.tone === "err"
              ? "text-rose-700 bg-rose-50 border-rose-200"
              : "text-neutral-600 bg-neutral-50 border-neutral-200";
          return (
            <div
              key={f.id}
              className="rounded-xl border border-border bg-white p-3 flex items-start gap-3"
              data-testid={`car-channel-sync-feed-${f.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-sm truncate" data-testid="car-channel-sync-feed-label">{f.label}</div>
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 px-1.5 py-0.5 rounded-full bg-neutral-100">
                    {(presets.find((p) => p.key === f.channel) || {}).label || f.channel}
                  </span>
                </div>
                <div className="text-[11px] text-neutral-500 truncate mt-0.5 font-mono">{f.url}</div>
                <div className={`mt-2 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${toneCls}`}>
                  <StatusIcon className="h-3 w-3" />
                  {s.label}
                  <span className="font-normal text-neutral-500 ml-1">· {_formatLastSync(f.last_synced_at)} · {f.last_event_count || 0} events</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => syncNow(f.id)}
                  disabled={busy}
                  className="h-8 w-8 rounded-lg hover:bg-neutral-100 text-neutral-600 flex items-center justify-center"
                  aria-label="Sync now"
                  data-testid={`car-channel-sync-feed-sync-${f.id}`}
                >
                  <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
                </button>
                <button
                  type="button"
                  onClick={() => deleteFeed(f.id, f.label)}
                  disabled={busy}
                  className="h-8 w-8 rounded-lg hover:bg-rose-50 text-rose-600 flex items-center justify-center"
                  aria-label="Remove feed"
                  data-testid={`car-channel-sync-feed-delete-${f.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add new feed */}
      {!atLimit ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3" data-testid="car-channel-sync-add">
          <Label className="text-xs uppercase tracking-wider font-bold text-amber-800">
            Add import feed
          </Label>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mt-2">
            <select
              value={addChannel}
              onChange={(e) => {
                setAddChannel(e.target.value);
                if (!addLabel) {
                  const preset = presets.find((p) => p.key === e.target.value);
                  if (preset && preset.key !== "custom") setAddLabel(preset.label);
                }
              }}
              className="md:col-span-3 rounded-xl border border-input bg-white px-3 py-2 text-sm"
              data-testid="car-channel-sync-add-channel"
            >
              {presets.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
            <Input
              placeholder="Label (e.g. Booking.com)"
              value={addLabel}
              onChange={(e) => setAddLabel(e.target.value)}
              className="md:col-span-3 rounded-xl"
              data-testid="car-channel-sync-add-label"
            />
            <Input
              placeholder="https://…/calendar.ics"
              value={addUrl}
              onChange={(e) => setAddUrl(e.target.value)}
              className="md:col-span-5 rounded-xl font-mono text-xs"
              data-testid="car-channel-sync-add-url"
            />
            <Button
              type="button"
              onClick={addFeed}
              disabled={busy}
              className="md:col-span-1 rounded-xl bg-amber-500 hover:bg-amber-600 text-amber-950"
              data-testid="car-channel-sync-add-btn"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {addError && (
            <div className="text-[11px] text-rose-600 mt-2" data-testid="car-channel-sync-add-error">{addError}</div>
          )}
          <p className="text-[11px] text-neutral-500 mt-2">
            {feeds.length}/{maxFeeds} feeds used. We poll every 15 min and rewrite blocks tagged with this feed.
          </p>
        </div>
      ) : (
        <div className="text-[11px] text-neutral-500 italic" data-testid="car-channel-sync-limit">
          Max {maxFeeds} import feeds per car reached — remove one to add another.
        </div>
      )}
    </div>
  );
}
